import type { EnvironmentProviders } from '@angular/core';
import { isDevMode } from '@angular/core';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  getMe,
  setIncidentMonitoringMuted,
  setInterfaceLanguage,
} from './effects/user-settings.effect';
import {
  getUserProfile,
  logout,
  redirectAfterProfileLoaded,
  setStoreSession,
} from './effects/user.effects';
import {
  socialLoginByCode,
  startLoadRequiredData,
} from './effects/auth.effects';
import {
  connectSocketOnRequiredDataLoaded,
  disconnectSocketOnLogout,
} from './effects/socket.effects';
import {
  loadOrgUsers,
  loadUsersByIds,
  loadOrgUsersOnHomeEnter,
  loadOrgUsersOnRequiredDataLoaded,
} from './effects/org-users.effects';
import {
  loadAccountsMap,
  loadAccountsMapOnHomeEnter,
  resetAccountsMapOnHomeLeave,
} from './effects/accounts-map.effects';
import { organizationFeature } from './state/organization/organization.feature';
import { loadActiveOrganization } from './effects/organization.effect';
import { userFeature } from './state/user/user.feature';
import { userSettingsFeature } from './state/user-settings/user-settings.feature';
import { connectivityFeature } from './state/connectivity/connectivity.feature';
import {
  inactivityTicks$,
  logoutCleanup$,
  registerUserActivity$,
} from './effects/connectivity.effects';
import { permissionsFeature } from './state/permissions/permissions.feature';
import { getPermissions$ } from './effects/permissions.effects';
import { routeFeature } from './state/route/route.reducer';
import { routerEvents$ } from './effects/router.effects';
import { orgUsersFeature } from './state/org-users/org-users.feature';
import { accountsMapFeature } from './state/accounts-map/accounts-map.feature';
import { contactsFeature } from './state/contacts/contacts.feature';
import {
  loadOrgContacts,
  loadAccountContactGroups,
} from './effects/contacts.effects';
import {
  loadIncidents$,
  loadIncidentsRequest$,
  loadPanicAlerts$,
} from './effects/incidents.effects';
import { incidentsFeature } from './state/incidents/incidents.reducer';
import { sopsFeature } from './state/sops/sops.feature';
import {
  startCreateSop,
  startDeleteSop,
  startDuplicateSop,
  startLoadAccountSops,
  startLoadOrganizationSops,
  startUpdateSop,
} from './effects/sops.effects';
import {
  getNextOrgWallsPage,
  loadOrgWalls,
  loadOrgWallsOnWallsEnter,
  loadOrgWallsRequest,
  resetOrgWallsOnWallsLeave,
} from './effects/org-walls.effects';
import { orgWallsFeature } from './state/org-walls/org-walls.feature';

export const provideAppState = (): EnvironmentProviders[] => [
  provideStore({}),
  provideState(connectivityFeature),
  provideState(userFeature),
  provideState(userSettingsFeature),
  provideState(organizationFeature),
  provideState(permissionsFeature),
  provideState(orgUsersFeature),
  provideState(accountsMapFeature),
  provideState(contactsFeature),
  provideState(routeFeature),
  provideState(incidentsFeature),
  provideState(sopsFeature),
  provideState(orgWallsFeature),
  provideEffects({
    getMe,
    setInterfaceLanguage,
    setIncidentMonitoringMuted,
  }),
  provideEffects({
    loadActiveOrganization,
  }),
  provideEffects({
    registerUserActivity$,
    inactivityTicks$,
    logoutCleanup$,
  }),
  provideEffects({
    startLoadRequiredData,
    getUserProfile,
    setStoreSession,
    redirectAfterProfileLoaded,
    logout,
    socialLoginByCode,
  }),
  provideEffects({
    getPermissions$,
  }),
  provideEffects({
    routerEvents$,
  }),
  provideEffects({
    connectSocketOnRequiredDataLoaded,
    disconnectSocketOnLogout,
  }),
  provideEffects({
    loadOrgUsersOnRequiredDataLoaded,
    loadOrgUsersOnHomeEnter,
    loadOrgUsers,
    loadUsersByIds,
  }),
  provideEffects({
    loadAccountsMapOnHomeEnter,
    resetAccountsMapOnHomeLeave,
    loadAccountsMap,
  }),
  provideEffects({
    loadOrgContacts,
    loadAccountContactGroups,
  }),
  provideEffects({
    loadIncidents$,
    loadIncidentsRequest$,
    loadPanicAlerts$,
  }),
  provideEffects({
    loadOrgWallsOnWallsEnter,
    resetOrgWallsOnWallsLeave,
    loadOrgWalls,
    getNextOrgWallsPage,
    loadOrgWallsRequest,
  }),
  provideEffects({
    startLoadOrganizationSops,
    startLoadAccountSops,
    startCreateSop,
    startUpdateSop,
    startDeleteSop,
    startDuplicateSop,
  }),
  provideRouterStore({
    stateKey: 'router',
    routerState: RouterState.Minimal,
  }),
  provideStoreDevtools({
    maxAge: 25,
    logOnly: !isDevMode(),
    autoPause: true,
    trace: false,
  }),
];
